
import React, { useMemo } from 'react';
import { sanitizeRxRy, stripScripts } from '../lib/svgGenerator';

interface DynamicSvgIconProps extends React.SVGProps<SVGSVGElement> {
  svgString: string;
}

const LARGE_SVG_THRESHOLD = 50000; // 50 KB

const DynamicSvgIcon: React.FC<DynamicSvgIconProps> = ({ svgString, className, style, ...props }) => {
  const isBase64 = svgString?.startsWith('data:image/svg+xml;base64,');

  // Memoize the parsing logic so it doesn't run on every re-render unless svgString changes
  const { viewBox, innerSvg, imgSrc } = useMemo(() => {
      if (isBase64 || !svgString || typeof svgString !== 'string') {
          return { viewBox: '0 0 24 24', innerSvg: null, imgSrc: null };
      }

      // Large SVGs: render as <img> to avoid parsing a huge DOM tree via dangerouslySetInnerHTML
      if (svgString.length > LARGE_SVG_THRESHOLD) {
          return { viewBox: '0 0 24 24', innerSvg: null, imgSrc: `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svgString)}` };
      }

      let raw = svgString.trim();
      let vb = '0 0 24 24';

      // Extract viewBox if present
      const match = raw.match(/viewBox=["']([^"']+)["']/);
      if (match && match[1]) {
          vb = match[1];
      }

      // Extract the inner content of the SVG, removing the outer <svg> tags.
      const content = stripScripts(sanitizeRxRy(raw.replace(/<svg[^>]*>|<\/svg>/g, '')));

      return { viewBox: vb, innerSvg: content, imgSrc: null };
  }, [svgString, isBase64]);

  // Base64-encoded or large SVGs: render as <img> to avoid expensive DOM parsing
  if (isBase64) {
    return <img src={svgString} className={className} style={style} />;
  }

  if (imgSrc) {
    return <img src={imgSrc} className={className} style={style} />;
  }

  if (!innerSvg) return null;

  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox={viewBox}
      className={className}
      style={style}
      {...props}
      dangerouslySetInnerHTML={{ __html: innerSvg }}
    />
  );
};

// React.memo prevents the component from re-rendering if props haven't changed
export default React.memo(DynamicSvgIcon);
